import { differenceInMinutes } from 'date-fns';

import { formatDuration } from './formatDuration';

import type { DurationUnit, Locale } from 'date-fns';

type FormatIntervalOptions = {
  /** Duration units to include in output (defaults to ['hours', 'minutes']) */
  format?: DurationUnit[];
  /** date-fns locale object */
  locale?: Locale;
};

/**
 * Calculate and format duration between two dates
 *
 * @param startTime - Start date (Date object or ISO string)
 * @param endTime - End date (Date object or ISO string)
 * @param options - Formatting options
 * @param options.locale - date-fns locale object
 * @param options.format - Duration units to include (defaults to ['hours', 'minutes'])
 *
 * @returns Formatted duration string (e.g., "30 хвилин", "1 година 30 хвилин")
 *
 * @example
 * ```ts
 * import { uk } from 'date-fns/locale'
 *
 * // 30 minutes
 * formatInterval('2025-01-01T10:00:00', '2025-01-01T10:30:00', { locale: uk })
 * // => "30 хвилин"
 *
 * // 1 hour 30 minutes
 * formatInterval('2025-01-01T10:00:00', '2025-01-01T11:30:00', { locale: uk })
 * // => "1 година 30 хвилин"
 *
 * // Only minutes (90 minutes total)
 * formatInterval('2025-01-01T10:00:00', '2025-01-01T11:30:00', { locale: uk, format: ['minutes'] })
 * // => "90 хвилин"
 * ```
 */
export const formatInterval = (
  startTime: Date | string,
  endTime: Date | string,
  { format = ['hours', 'minutes'], locale }: FormatIntervalOptions = {},
): string => {
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = typeof endTime === 'string' ? new Date(endTime) : endTime;

  // If format is only minutes, calculate total minutes directly
  if (format.length === 1 && format[0] === 'minutes') {
    const totalMinutes = differenceInMinutes(end, start);

    return formatDuration({ minutes: totalMinutes }, { format, locale });
  }

  // If format is only hours, calculate total hours directly
  if (format.length === 1 && format[0] === 'hours') {
    const totalMinutes = differenceInMinutes(end, start);
    const totalHours = Math.floor(totalMinutes / 60);

    return formatDuration({ hours: totalHours }, { format, locale });
  }

  // Default: split into hours and minutes
  const totalMinutes = differenceInMinutes(end, start);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return formatDuration({ hours, minutes }, { format, locale });
};
